// ABOUTME: Delivery Window configuration form — business days, slot management days, operating hours, and delivery constraints.
// ABOUTME: Each section label has an ⓘ tooltip; read-only when scope is below country.
import {Icon, TextMedium} from '@hm/fundamental'
import {CountryConfigsTooltip} from './CountryConfigsTooltip'
import {mdiInformationOutline} from '@mdi/js'
import React, {useCallback, useEffect, useId, useMemo, useState} from 'react'
import {Controller, useForm, useWatch} from 'react-hook-form'

import {useTranslation} from '../../../../hooks/useTranslation'
import {CONSTRAINT_FIELDS, ConstraintField, DAY_FULL_LABELS, EMPTY_HOURS} from '../constants'
import {OperatingHoursColumn, OperatingHoursRow as OHRow} from '../types/operatingHoursTable'
import {
  DAYS,
  DeliveryWindowFormProps,
  DeliveryWindowFormValues,
  DeliveryWindowOverrides,
  DayIdentifier,
} from '../types/deliveryWindow'
import {formatMinutesAsTime, parseTimeToMinutes, toFormValues, validateHours} from '../utils'
import {dayCodesToIdentifiers} from '../utils/entityCatalogOptions'
import {useTimeZoneValidation} from '../hooks/useTimeZoneValidation'
import {DayChipToggle} from './DayChipToggle'
import {CountryConfigsTimezoneSelect} from '../CountryConfigsTimezoneSelect'
import {NumericalInput} from './NumericalInput'
import {OperatingHoursTable} from './OperatingHoursTable'
import {OverrideDot} from './OverrideDot'
import {SectionTitle} from './SectionTitle'

export const DeliveryWindowForm: React.FC<DeliveryWindowFormProps> = ({
  config,
  isReadOnly,
  isSlotManagementEnabled,
  isCountryDefault,
  onDirtyChange,
  onValidChange,
  onValuesChange,
  dayCodes,
}) => {
  const showDot = !isCountryDefault
  const {t} = useTranslation()
  const timeZoneLabelId = useId()
  const timeZoneErrorId = useId()
  const [touchedNonStandardDays, setTouchedNonStandardDays] = useState<Set<DayIdentifier>>(() => {
    const existing = DAYS.filter(
      (d) => config.businessDays.includes(d) && !config.standardOperatingDays.includes(d)
    )
    return new Set(existing)
  })
  const allowedDays = useMemo(() => dayCodesToIdentifiers(dayCodes) ?? DAYS, [dayCodes])
  const formDefaultValues = useMemo(() => toFormValues(config), [config])
  const {control, setValue, getValues, reset, formState: {isDirty, dirtyFields}} = useForm<DeliveryWindowFormValues>({
    defaultValues: formDefaultValues,
  })

  useEffect(() => {
    reset(formDefaultValues)
  }, [formDefaultValues, reset])

  const timeZone = useWatch({control, name: 'timeZone'})
  const businessDays = useWatch({control, name: 'businessDays'})
  const slotManagementDays = useWatch({control, name: 'slotManagementDays'})
  const standardOperatingDays = useWatch({control, name: 'standardOperatingDays'})
  const standardHours = useWatch({control, name: 'standardHours'})
  const nonStandardHours = useWatch({control, name: 'nonStandardHours'})
  const deliveryConstraints = useWatch({control, name: 'deliveryConstraints'})

  // Render the union of backend-allowed days and currently-selected days (in canonical order) so a
  // day that is selected in the saved config but missing from the backend dayCodes still shows a
  // chip and stays removable. Selected days span businessDays (slot/standard are subsets of it).
  const availableDays = useMemo(
    () => DAYS.filter((d) => allowedDays.includes(d) || businessDays.includes(d)),
    [allowedDays, businessDays]
  )

  // Non-standard days = businessDays that are NOT in standardOperatingDays
  const nonStandardDays = useMemo(
    () => DAYS.filter((d) => businessDays.includes(d) && !standardOperatingDays.includes(d)),
    [businessDays, standardOperatingDays]
  )

  useEffect(() => {
    setTouchedNonStandardDays((prev) => {
      // Always show errors for days that were non-standard in the loaded config
      const configNonStandard = DAYS.filter(
        (d) => config.businessDays.includes(d) && !config.standardOperatingDays.includes(d)
      )
      const next = new Set(
        [...prev, ...configNonStandard].filter((d) => nonStandardDays.includes(d as DayIdentifier))
      )
      if (next.size === prev.size && [...next].every((d) => prev.has(d))) return prev
      return next
    })
  }, [nonStandardDays, config])

  const nonStandardRows: OHRow[] = useMemo(() => {
    const slotMgmtDisabledText = t('countryConfigurations.deliveryWindow.nonStandardHours.slotMgmtDisabled')
    const isOverridden = !isCountryDefault && config.overrides.operatingSchedule
    return nonStandardDays.map((day) => {
      const hours = nonStandardHours[day] ?? EMPTY_HOURS
      const isSlotMgmtDay = slotManagementDays.includes(day)
      return {
        key: day,
        label: t(DAY_FULL_LABELS[day]),
        values: hours,
        disabledColumns: isSlotMgmtDay ? undefined : {slotManagement: slotMgmtDisabledText},
        isOverridden,
      }
    })
  }, [nonStandardDays, nonStandardHours, slotManagementDays, t, isCountryDefault, config.overrides.operatingSchedule])

  const hoursErrors = useMemo(
    () => validateHours([{timeRanges: standardHours}], t, {requiredColumns: ['onlineDelivery', 'defaultDelivery']}),
    [standardHours, t]
  )

  const nonStandardErrors = useMemo(
    () => validateHours(nonStandardRows.map((row) => ({timeRanges: row.values as Record<string, {start: string; end: string}>})), t, {requiredColumns: ['onlineDelivery', 'defaultDelivery']}),
    [nonStandardRows, t]
  )

  // Transform index-based error keys (from validateHours) to row-key-based keys (for OperatingHoursTable)
  const hoursErrorsMapped = useMemo(() => {
    const mapped: Record<string, string> = {}
    for (const [key, msg] of Object.entries(hoursErrors)) {
      mapped[key.replace(/^0\./, 'standard.')] = msg
    }
    return mapped
  }, [hoursErrors])

  const nonStandardErrorsMapped = useMemo(() => {
    const mapped: Record<string, string> = {}
    for (const [key, msg] of Object.entries(nonStandardErrors)) {
      const dotIndex = key.indexOf('.')
      const rowIndex = parseInt(key.substring(0, dotIndex), 10)
      const rest = key.substring(dotIndex + 1)
      const day = nonStandardDays[rowIndex]
      if (day && touchedNonStandardDays.has(day)) {
        mapped[`${day}.${rest}`] = msg
      }
    }
    return mapped
  }, [nonStandardErrors, nonStandardDays, touchedNonStandardDays])

  const constraintErrors = useMemo<Partial<Record<ConstraintField, string>>>(() => {
    const errs: Partial<Record<ConstraintField, string>> = {}
    for (const {key} of CONSTRAINT_FIELDS) {
      if (deliveryConstraints[key] <= 0) {
        errs[key] = t('countryConfigurations.deliveryWindow.deliveryConstraints.errorNonNegative')
      }
    }
    return errs
  }, [deliveryConstraints, t])

  const standardDaysSubsetError = useMemo(() => {
    const parentDays = isSlotManagementEnabled ? slotManagementDays : businessDays
    const invalidDays = standardOperatingDays.filter(d => !parentDays.includes(d))
    return invalidDays.length > 0
  }, [standardOperatingDays, slotManagementDays, businessDays, isSlotManagementEnabled])

  const timeZoneError = useTimeZoneValidation(
    timeZone ?? '',
    Boolean(dirtyFields.timeZone),
    t('countryConfigurations.deliveryWindow.timeZone.invalid'),
  )

  const businessDaysError = businessDays.length === 0
    ? t('countryConfigurations.deliveryWindow.validation.businessDaysRequired')
    : undefined
  const standardDaysError = standardOperatingDays.length === 0
    ? t('countryConfigurations.deliveryWindow.validation.standardDaysRequired')
    : undefined

  const isValid =
    Object.keys(hoursErrors).length === 0 &&
    Object.keys(nonStandardErrors).length === 0 &&
    Object.keys(constraintErrors).length === 0 &&
    !standardDaysSubsetError &&
    !businessDaysError &&
    !standardDaysError &&
    !timeZoneError

  useEffect(() => {
    onDirtyChange(isDirty)
  }, [isDirty, onDirtyChange])

  useEffect(() => {
    onValidChange(isValid)
  }, [isValid, onValidChange])

  const values = useMemo<DeliveryWindowFormValues>(() => ({
    timeZone,
    businessDays,
    slotManagementDays,
    standardOperatingDays,
    standardHours,
    nonStandardHours,
    deliveryConstraints,
  }), [timeZone, businessDays, slotManagementDays, standardOperatingDays, standardHours, nonStandardHours, deliveryConstraints])

  useEffect(() => {
    onValuesChange?.(values)
  }, [values, onValuesChange])

  const handleBusinessDaysChange = useCallback((next: DayIdentifier[]) => {
    const prev = getValues()
    setValue('businessDays', next, {shouldDirty: true})

    if (isSlotManagementEnabled) {
      const validSlotDays = prev.slotManagementDays.filter((d) => next.includes(d))
      const sortedSlotDays = DAYS.filter((d) => validSlotDays.includes(d))
      setValue('slotManagementDays', sortedSlotDays, {shouldDirty: true})

      const validOpDays = prev.standardOperatingDays.filter((d) => sortedSlotDays.includes(d))
      const sortedOpDays = DAYS.filter((d) => validOpDays.includes(d))
      setValue('standardOperatingDays', sortedOpDays, {shouldDirty: true})

      const validNonStandard = Object.fromEntries(
        Object.entries(prev.nonStandardHours).filter(([d]) => next.includes(d as DayIdentifier) && !sortedOpDays.includes(d as DayIdentifier))
      )
      setValue('nonStandardHours', validNonStandard, {shouldDirty: true})
    } else {
      const validOpDays = prev.standardOperatingDays.filter((d) => next.includes(d))
      const sortedOpDays = DAYS.filter((d) => validOpDays.includes(d))
      setValue('standardOperatingDays', sortedOpDays, {shouldDirty: true})

      const validNonStandard = Object.fromEntries(
        Object.entries(prev.nonStandardHours).filter(([d]) => next.includes(d as DayIdentifier) && !sortedOpDays.includes(d as DayIdentifier))
      )
      setValue('nonStandardHours', validNonStandard, {shouldDirty: true})
    }
  }, [getValues, setValue, isSlotManagementEnabled])

  const handleSlotManagementDaysChange = useCallback((next: DayIdentifier[]) => {
    const prev = getValues()
    setValue('slotManagementDays', next, {shouldDirty: true})

    const validOpDays = prev.standardOperatingDays.filter((d) => next.includes(d))
    const sortedOpDays = DAYS.filter((d) => validOpDays.includes(d))
    setValue('standardOperatingDays', sortedOpDays, {shouldDirty: true})
  }, [getValues, setValue])

  const handleStandardDaysChange = useCallback((next: DayIdentifier[]) => {
    const prev = getValues()
    setValue('standardOperatingDays', next, {shouldDirty: true})

    const validNonStandard = Object.fromEntries(
      Object.entries(prev.nonStandardHours).filter(([d]) => !next.includes(d as DayIdentifier))
    )
    setValue('nonStandardHours', validNonStandard, {shouldDirty: true})
  }, [getValues, setValue])

  const handleTimeChange = useCallback(
    (columnKey: string, field: 'start' | 'end', value: string) => {
      const prev = getValues()
      setValue('standardHours', {
        ...prev.standardHours,
        [columnKey]: {
          ...prev.standardHours[columnKey],
          [field]: value,
        },
      }, {shouldDirty: true})
    },
    [getValues, setValue]
  )

  const handleNonStandardTimeChange = useCallback(
    (rowKey: string, columnKey: string, value: string, field?: 'start' | 'end') => {
      const day = rowKey as DayIdentifier
      const prev = getValues()
      setValue('nonStandardHours', {
        ...prev.nonStandardHours,
        [day]: {
          ...(prev.nonStandardHours[day] ?? EMPTY_HOURS),
          [columnKey]: {
            ...(prev.nonStandardHours[day]?.[columnKey] ?? {start: '', end: ''}),
            [field!]: value,
          },
        },
      }, {shouldDirty: true})
      setTouchedNonStandardDays((prev) => new Set(prev).add(day))
    },
    [getValues, setValue]
  )

  const constraintHandlers = useMemo(
    () => Object.fromEntries(
      CONSTRAINT_FIELDS.map(({key}) => [key, (val: number) => {
        const prev = getValues()
        setValue('deliveryConstraints', {...prev.deliveryConstraints, [key]: val}, {shouldDirty: true})
      }])
    ) as Record<ConstraintField, (val: number) => void>,
    [getValues, setValue]
  )

  // Days not in businessDays should be disabled for slot management toggle
  const slotDisabledDays = useMemo(
    () => DAYS.filter((d) => !businessDays.includes(d)),
    [businessDays]
  )

  // Days not in slotManagementDays (or businessDays when slot mgmt disabled) should be disabled for operating hours toggle
  const operatingHoursDisabledDays = useMemo(
    () => DAYS.filter((d) => !(isSlotManagementEnabled ? slotManagementDays : businessDays).includes(d)),
    [slotManagementDays, businessDays, isSlotManagementEnabled]
  )

  const operatingHoursColumns: OperatingHoursColumn[] = useMemo(
    () => [
      {
        key: 'onlineDelivery',
        label: t('countryConfigurations.deliveryWindow.operatingHours.onlineDeliveryHours'),
        tooltip: t('countryConfigurations.deliveryWindow.operatingHours.onlineDeliveryTooltip'),
      },
      {
        key: 'defaultDelivery',
        label: t('countryConfigurations.deliveryWindow.operatingHours.defaultDeliveryHours'),
        tooltip: t('countryConfigurations.deliveryWindow.operatingHours.defaultDeliveryTooltip'),
      },
      ...(isSlotManagementEnabled
        ? [
            {
              key: 'slotManagement',
              label: t('countryConfigurations.deliveryWindow.operatingHours.slotManagementHours'),
              tooltip: t('countryConfigurations.deliveryWindow.operatingHours.slotManagementTooltip'),
            },
          ]
        : []),
    ],
    [t, isSlotManagementEnabled]
  )

  return (
    <div className="flex flex-col gap-6 p-6 w-full" data-cy="delivery-window-form">
      <div className="flex gap-6 items-center">
        <div className="flex items-center gap-1 min-w-[200px] max-w-[300px]">
          <span id={timeZoneLabelId} className="text-base font-medium text-foreground">{t('countryConfigurations.deliveryWindow.timeZone.label')}</span>
          <CountryConfigsTooltip content={t('countryConfigurations.deliveryWindow.timeZone.tooltip')} delayMs={300}>
            <span className="self-center shrink-0 flex cursor-default" aria-label="More information">
              <Icon path={mdiInformationOutline} size="regular" className="text-foreground" />
            </span>
          </CountryConfigsTooltip>
        </div>
        <div className="flex flex-col gap-1">
          <Controller
            name="timeZone"
            control={control}
            render={({field}) => (
              <CountryConfigsTimezoneSelect
                value={field.value}
                onChange={field.onChange}
                disabled={isReadOnly}
                extraOptions={[config.timeZone]}
                placeholderKey="countryConfigurations.deliveryWindow.timeZone.placeholder"
                searchPlaceholderKey="countryConfigurations.deliveryWindow.timeZone.searchPlaceholder"
                noResultsKey="countryConfigurations.deliveryWindow.timeZone.noResults"
                aria-labelledby={timeZoneLabelId}
                aria-describedby={timeZoneError ? timeZoneErrorId : undefined}
              />
            )}
          />
          {timeZoneError && <p id={timeZoneErrorId} role="alert" className="text-xs leading-4 text-negative px-3">{timeZoneError}</p>}
        </div>
      </div>
      <hr className="border-border" />

      {/* Business days section */}
      <div className="flex flex-col gap-4">
        <SectionTitle
          titleKey="countryConfigurations.deliveryWindow.businessDays.title"
          tooltipKey="countryConfigurations.deliveryWindow.businessDays.tooltip"
        />
        <DayChipToggle
          value={businessDays}
          onChange={handleBusinessDaysChange}
          days={availableDays}
          disabled={isReadOnly}
          data-cy="business-days"
        />
        {businessDaysError && <p role="alert" className="text-xs leading-4 text-negative">{businessDaysError}</p>}
      </div>

      {isSlotManagementEnabled && (
        <>
          <hr className="border-border" />
          <div className="flex flex-col gap-4">
            <SectionTitle
              titleKey="countryConfigurations.deliveryWindow.slotManagementDays.title"
              tooltipKey="countryConfigurations.deliveryWindow.slotManagementDays.tooltip"
            />
            <DayChipToggle
              value={slotManagementDays}
              onChange={handleSlotManagementDaysChange}
              days={availableDays}
              disabled={isReadOnly}
              disabledDays={slotDisabledDays}
              data-cy="slot-management-days"
            />
          </div>
        </>
      )}

      <hr className="border-border" />

      {/* Operating hours section */}
      <div className="flex flex-col gap-4">
        <SectionTitle
          titleKey="countryConfigurations.deliveryWindow.operatingHours.title"
          tooltipKey="countryConfigurations.deliveryWindow.operatingHours.tooltip"
          subtitle="countryConfigurations.deliveryWindow.operatingHours.subtitle"
        />

        <div className="flex flex-col gap-2">
          <span className="text-sm font-medium text-foreground">
            {t('countryConfigurations.deliveryWindow.operatingHours.selectStandardDays')}
          </span>
          <DayChipToggle
            value={standardOperatingDays}
            onChange={handleStandardDaysChange}
            days={availableDays}
            disabled={isReadOnly}
            disabledDays={operatingHoursDisabledDays}
            data-cy="standard-operating-days"
          />
          {standardDaysError && <p role="alert" className="text-xs leading-4 text-negative">{standardDaysError}</p>}
        </div>

        <OperatingHoursTable
          columns={operatingHoursColumns}
          rows={[
            {
              key: 'standard',
              label: t('countryConfigurations.deliveryWindow.operatingHours.standardHours'),
              values: standardHours,
              isOverridden: showDot && config.overrides.operatingSchedule,
            },
          ]}
          onTimeChange={(_rowKey, columnKey, value, field) =>
            handleTimeChange(columnKey, field!, value)
          }
          disabled={isReadOnly}
          errors={hoursErrorsMapped}
          firstColumnLabel={t('countryConfigurations.deliveryWindow.operatingHours.weekDay')}
          data-cy="operating-hours-table"
        />
      </div>

      <hr className="border-border" />

      {/* Non-standard operating hours section */}
      <div className="flex flex-col gap-4">
        <SectionTitle
          titleKey="countryConfigurations.deliveryWindow.nonStandardHours.title"
          tooltipKey="countryConfigurations.deliveryWindow.nonStandardHours.tooltip"
          subtitle="countryConfigurations.deliveryWindow.nonStandardHours.subtitle"
        />

        {nonStandardRows.length > 0 ? (
          <OperatingHoursTable
            columns={operatingHoursColumns}
            rows={nonStandardRows}
            onTimeChange={handleNonStandardTimeChange}
            disabled={isReadOnly}
            errors={nonStandardErrorsMapped}
            firstColumnLabel={t('countryConfigurations.deliveryWindow.operatingHours.weekDay')}
            data-cy="non-standard-hours-table"
          />
        ) : (
          <span className="text-sm text-foreground_faded">
            {t('countryConfigurations.deliveryWindow.nonStandardHours.emptyState')}
          </span>
        )}
      </div>

      <hr className="border-border" />

      {/* Delivery constraints section */}
      <div className="flex flex-col gap-6" data-cy="delivery-constraints-section">
        <SectionTitle
          titleKey="countryConfigurations.deliveryWindow.deliveryConstraints.title"
        />

        {CONSTRAINT_FIELDS.map((field) => (
          <div key={field.key} className="flex gap-14 items-start w-full">
            <div className="flex flex-col justify-center shrink-0 h-10 w-60">
              <div className="flex gap-0.5 items-center whitespace-nowrap">
                {showDot && config.overrides[field.key as keyof DeliveryWindowOverrides] && <OverrideDot />}
                <TextMedium className="text-foreground line-clamp-1">{t(field.labelKey)}</TextMedium>
                <CountryConfigsTooltip content={t(field.tooltipKey)} delayMs={300}>
                  <span className="text-foreground flex items-center cursor-default">
                    <Icon path={mdiInformationOutline} size="regular" />
                  </span>
                </CountryConfigsTooltip>
              </div>
            </div>

            <NumericalInput
              size="large"
              value={deliveryConstraints[field.key]}
              onChange={constraintHandlers[field.key]}
              min={0}
              step={field.key === 'stepInterval' ? 30 : (values.deliveryConstraints.stepInterval || 30)}
              suffix="h"
              disabled={isReadOnly}
              error={constraintErrors[field.key]}
              data-cy={`delivery-constraint-${field.key}`}
              formatValue={formatMinutesAsTime}
              parseValue={parseTimeToMinutes}
            />
          </div>
        ))}
      </div>
    </div>
  )
}
